import { Link } from "react-router-dom";
import { MessageCircle, Sparkles, HeartHandshake } from "lucide-react";

/**
 * Trust strip rendered at the bottom of both /shop and the product detail
 * page. Three calm CTAs — assessment, mentor, WhatsApp — laid out with
 * generous whitespace so it never feels like an upsell band.
 */
export default function TrustStrip({ variant = "full" }) {
  return (
    <section
      className="bg-emerald-50/40 border-t border-b border-emerald-100 py-12 sm:py-16"
      data-testid="shop-trust-strip"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {variant === "full" && (
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
              Guided wellness, gently
            </p>
            <h2 className="font-display text-3xl sm:text-4xl text-emerald-950 font-light mt-2 leading-tight">
              You're not alone in this choice.
            </h2>
            <p className="text-emerald-900/70 mt-3 max-w-xl mx-auto leading-relaxed">
              Three free paths to clarity before you commit to anything.
            </p>
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-3 sm:gap-4">
          <TrustCard
            icon={Sparkles}
            title="Take the free assessment"
            body="A 2-minute reflection that quietly tells us what your body is asking for."
            cta="Start assessment"
            href="/questionnaire"
            testid="trust-assessment"
          />
          <TrustCard
            icon={HeartHandshake}
            title="Talk to a wellness mentor"
            body="15 calm minutes with a real human. No pressure, no script."
            cta="Book a call"
            href="/contact?intent=mentor"
            testid="trust-mentor"
          />
          <TrustCard
            icon={MessageCircle}
            title="WhatsApp guidance"
            body="Tap and ask anything. Our coaches reply within a day, usually faster."
            cta="Open WhatsApp"
            href="https://wa.me/447424225333?text=Hi%2C%20I%27d%20like%20wellness%20guidance"
            external
            testid="trust-whatsapp"
          />
        </div>
      </div>
    </section>
  );
}

function TrustCard({ icon: Icon, title, body, cta, href, external, testid }) {
  const LinkTag = external ? "a" : Link;
  const linkProps = external
    ? { href, target: "_blank", rel: "noopener noreferrer" }
    : { to: href };
  return (
    <LinkTag
      {...linkProps}
      className="group block rounded-2xl bg-white border border-emerald-100 p-5 sm:p-6 hover:border-emerald-300 hover:shadow-sm transition"
      data-testid={testid}
    >
      <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-700 mb-3">
        <Icon className="w-4 h-4" strokeWidth={1.6} />
      </span>
      <h3 className="font-display text-lg text-emerald-950 font-light leading-snug">{title}</h3>
      <p className="text-[13px] text-emerald-900/70 mt-1.5 leading-relaxed">{body}</p>
      <span className="inline-block text-sm text-emerald-700 mt-3 group-hover:text-emerald-800 transition">
        {cta} →
      </span>
    </LinkTag>
  );
}
